import { useTheme } from "@mui/material";
import classnames from "classnames";

import { useMergeClasses } from "../useMergeClasses";

import { StyledInputLabel } from "./styles";
import { zeffyInputLabelClasses, InputLabelSsrProps } from "./types";

import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTooltip } from "@/dashboard-design-system/Tooltip/DashboardTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Info } from "@/icons/outlined";

export const DashboardInputLabelSsr: React.FC<InputLabelSsrProps> = ({
  children,
  classes: externalClasses,
  className,
  errorMessage,
  helperText,
  headerRight,
  label,
  sx,
  tooltipText,
  vibe = "neutral",
  "data-test": dataTest,
  forwardedRef,
}) => {
  const classes = useMergeClasses(zeffyInputLabelClasses, externalClasses);
  const theme = useTheme();

  return (
    <StyledInputLabel
      className={classnames(className, classes.container)}
      ref={forwardedRef}
      data-test={dataTest}
      sx={sx}
    >
      {(label || tooltipText || headerRight) && (
        <div className={classes.header}>
          <div className={classes.labelContainer}>
            {label && (
              <DashboardTypography
                variant="body2"
                className={classnames({
                  [classes.labelNeutral]: vibe === "neutral",
                  [classes.labelForm]: vibe !== "neutral",
                })}
                data-test="input-label-label-typography"
                sx={
                  theme.constants.isFormV2
                    ? { fontWeight: 600, color: `${theme.palette.text.form.intense} !important` }
                    : {}
                }
              >
                {label}
              </DashboardTypography>
            )}
            {tooltipText && (
              <DashboardTooltip title={tooltipText} placement="top">
                <DashboardIcon size="small" sx={{ cursor: "pointer" }}>
                  <Info />
                </DashboardIcon>
              </DashboardTooltip>
            )}
          </div>
          <DashboardTypography variant="caption" vibe="text-neutral-quiet" className={classes.headerRight}>
            {headerRight}
          </DashboardTypography>
        </div>
      )}
      {children}
      {errorMessage ? (
        <DashboardTypography
          component="p"
          variant="caption"
          vibe="text-danger-quiet"
          className={classnames(classes.footerText, classes.errorText)}
        >
          {errorMessage}
        </DashboardTypography>
      ) : helperText ? (
        <DashboardTypography
          component="p"
          variant="caption"
          vibe="text-brand-moderate"
          className={classnames(classes.footerText, classes.helperText)}
        >
          {helperText}
        </DashboardTypography>
      ) : (
        <></>
      )}
    </StyledInputLabel>
  );
};

DashboardInputLabelSsr.displayName = "DashboardInputLabel";
